/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { StorageService } from "../storage.service";

@Injectable({
	providedIn: "root"
})
export class AppStateService {
	public isAppDestroyed$ = new Subject<void>();

	private _hasError$ = new BehaviorSubject<boolean>(false);
	public hasError$ = this._hasError$.asObservable();

	private _isAppAccessible$ = new BehaviorSubject<boolean>(false);
	public isAppAccessible$ = this._isAppAccessible$.asObservable();

	private _dashboardLoadingContexts = new Set<string>();
	private _isDashboardLoading$ = new BehaviorSubject<boolean>(false);
	public isDashboardLoading$ = this._isDashboardLoading$.asObservable();

	constructor(private _storageService: StorageService) {}

	/**
	 * Updates the state to indicate whether an error has occurred.
	 *
	 * @param hasError - A boolean value indicating if there is an error (true) or not (false).
	 */
	public setHasError(hasError: boolean) {
		this._hasError$.next(hasError);
	}

	/**
	 * Updates the state to indicate whether the app is accessible.
	 *
	 * @param isAppAccessible - A boolean value indicating if the app is accessible (true) or not (false).
	 */
	public setIsAppAccessible(isAppAccessible: boolean) {
		this._isAppAccessible$.next(isAppAccessible);
		this._storageService.setApplicationAccessibilityState(isAppAccessible);
	}

	public setDashboardLoading(context: string, isLoading: boolean) {
		if (isLoading) this._dashboardLoadingContexts.add(context);
		else this._dashboardLoadingContexts.delete(context);

		this._isDashboardLoading$.next(this._dashboardLoadingContexts.size > 0);
	}

	/**
	 * Marks the application as destroyed by emitting a value to the `_isAppDestroyed$` subject.
	 * This can be used to signal cleanup or teardown operations in the application.
	 */
	public setIsAppDestroyed() {
		this.isAppDestroyed$.next();
		this.isAppDestroyed$.complete();
	}
}
